import React, { useEffect, useState } from 'react';
import { X, Check, Send, Clock, User, MessageSquare, Briefcase, Building2, AlertCircle, Plus, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';
import { Button } from './ui/Button';
export interface Job {
  emoji: string;
  title: string;
  subtitle: string;
  description: string;
  fullDescription?: {
    intro: string;
    tasks: string[];
    requirements: string[];
    callToAction: string;
  };
}
interface JobApplicationFormProps {
  job: Job;
  onClose: () => void;
}
const roleQuestions: Record<string, string[]> = {
  Bagare: ['Har du tidigare erfarenhet av bageri eller konditori? Berätta gärna kort.', 'Vad betyder kvalitet och hantverk för dig?', 'Du kommer in kl 04:00 och upptäcker att jäsningen på degen inte gått som planerat – hela morgonens produktion är påverkad. Vad gör du konkret de första 15 minuterna?', 'Berätta om en gång du märkte att en produkt inte höll den standard du ville ha. Vad gjorde du och varför?'],
  Platschef: ['Har du erfarenhet av både service och att leda ett team? Berätta gärna kort om båda delarna.', 'Hur hanterar du en stressig situation med både personal och kunder?', 'En av dina medarbetare presterar under sin förmåga och stämningen i teamet börjar påverkas. Hur tar du tag i det – steg för steg?', 'Du får feedback från en stamgäst att servicen blivit sämre den senaste veckan. Samtidigt märker du att personalen verkar omotiverad. Vad gör du – och i vilken ordning?'],
  Servicepersonal: ['Har du erfarenhet av service eller kundkontakt? Berätta gärna kort.', 'Beskriv hur du skulle hantera en missnöjd gäst.', 'En gäst frågar om en produkt du inte känner till – du är ensam i kassan och det är kö. Hur agerar du?', 'Ge ett konkret exempel på när du gick utöver det förväntade för att ge någon en bra upplevelse – på jobbet eller privat.']
};
const WEB3FORMS_KEY = '00888808-a120-4a58-b457-d0746e22e1c7';
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const textareaClass = 'w-full min-h-[80px] p-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans resize-y text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const required = <span className="text-red-500 ml-0.5" data-id="element-694" data-mp="3969fq74mfiu1">*</span>;
export function JobApplicationForm({
  job,
  onClose
}: JobApplicationFormProps) {
  const [step, setStep] = useState<'description' | 'form'>('description');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState<Record<string, string>>({
    name: '',
    age: '',
    phone: '',
    email: '',
    city: '',
    workAmount: '',
    hoursPerWeek: '',
    availabilityTimes: '',
    timePreferences: '',
    startDate: '',
    q1: '',
    q2: '',
    q3: '',
    q4: '',
    // Personality questions
    strength: '',
    weakness: '',
    definesYou: '',
    friendDescribes: '',
    employerDescribes: '',
    parentDescribes: '',
    // Previous workplace 1
    prev1_name: '',
    prev1_refName: '',
    prev1_refContact: '',
    prev1_summary: '',
    // Previous workplace 2
    prev2_name: '',
    prev2_refName: '',
    prev2_refContact: '',
    prev2_summary: '',
    // Previous workplace 3
    prev3_name: '',
    prev3_refName: '',
    prev3_refContact: '',
    prev3_summary: '',
    // Education
    edu_name: '',
    edu_school: '',
    edu_year: '',
    edu_description: '',
    // Personal intro
    personalIntro: ''
  });
  const [showWorkplace2, setShowWorkplace2] = useState(false);
  const [showWorkplace3, setShowWorkplace3] = useState(false);
  const [expandedWorkplaces, setExpandedWorkplaces] = useState<Record<number, boolean>>({
    1: true
  });
  const showPartTimeFields = formData.workAmount === 'Deltid' || formData.workAmount === 'Extra / vid behov';
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const questions = roleQuestions[job.title] || roleQuestions['Servicepersonal'];
    // Build a readable message for Web3Forms
    const payload: Record<string, string> = {
      access_key: WEB3FORMS_KEY,
      subject: `Ny ansökan: ${job.title} – ${formData.name}`,
      from_name: formData.name,
      replyto: formData.email,
      // Personal info
      Namn: formData.name,
      Ålder: formData.age,
      Telefon: formData.phone,
      'E-post': formData.email,
      Bostadsort: formData.city,
      'Önskad tjänst': `${job.emoji} ${job.title} (${job.subtitle})`,
      'Hur mycket vill personen jobba': formData.workAmount
    };
    if (showPartTimeFields) {
      payload['Antal timmar per vecka'] = formData.hoursPerWeek;
      payload['Tillgängliga tider'] = formData.availabilityTimes;
    }
    payload['Tidsönskemål'] = formData.timePreferences || '(Ej angivet)';
    payload['Kan börja från'] = formData.startDate || '(Ej angivet)';
    // Role-specific questions
    questions.forEach((q, i) => {
      payload[`Fråga ${i + 1}: ${q}`] = formData[`q${i + 1}`];
    });
    // Personality questions
    payload['Starkaste sida'] = formData.strength;
    payload['Svagaste sida'] = formData.weakness;
    payload['Vad definierar dig som person'] = formData.definesYou;
    payload['Bästa vännen beskriver dig som'] = formData.friendDescribes;
    payload['Arbetsgivaren beskriver dig som'] = formData.employerDescribes;
    payload['Föräldern beskriver dig som'] = formData.parentDescribes;
    // Previous workplaces
    for (let i = 1; i <= 3; i++) {
      const prefix = `prev${i}`;
      const pName = formData[`${prefix}_name`];
      if (pName) {
        payload[`Arbetsplats ${i} – Namn`] = pName;
        payload[`Arbetsplats ${i} – Referens namn`] = formData[`${prefix}_refName`];
        payload[`Arbetsplats ${i} – Referens kontakt`] = formData[`${prefix}_refContact`];
        payload[`Arbetsplats ${i} – Om stället, uppgifter & upplevelse`] = formData[`${prefix}_summary`];
      }
    }
    // Education
    if (formData.edu_name) {
      payload['Utbildning / Kurs'] = formData.edu_name;
      payload['Utbildning – Skola / Anordnare'] = formData.edu_school || '(Ej angivet)';
      payload['Utbildning – År'] = formData.edu_year || '(Ej angivet)';
      payload['Utbildning – Beskrivning'] = formData.edu_description || '(Ej angivet)';
    }
    payload['Personligt intro'] = formData.personalIntro || '(Ej angivet)';
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss direkt.');
      }
    } catch {
      setSubmitError('Kunde inte skicka ansökan. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const questions = roleQuestions[job.title] || roleQuestions['Servicepersonal'];
  if (isSuccess) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-695" data-mp="27lcr7dw27uiq">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200" data-id="element-696" data-mp="2cd351tu7h0ew">
          <div className="w-16 h-16 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-697" data-mp="2hdii0kaks5wc">
            <Check className="w-8 h-8 text-[#C5A55A]" data-id="element-698" data-mp="1on0ift928mno" />
          </div>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-3" data-id="element-699" data-mp="1on8ujixcznxt">
            Tack för din ansökan!
          </h3>
          <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed" data-id="element-700" data-mp="1tog1jfrogj3f">
            Tack för att du vill jobba hos oss! Vi kommer att gå igenom alla
            ansökningar och höra av oss om vi vill gå vidare till nästa steg.
            Tack!
          </p>
          <Button onClick={onClose} className="w-full" data-id="element-701" data-mp="39zacdbhw96uy">
            Stäng fönstret
          </Button>
        </div>
      </div>;
  }
  const SectionHeading = ({
    icon: Icon,
    title
  }: {
    icon: React.ElementType;
    title: string;
  }) => <div className="flex items-center gap-3 border-l-4 border-[#C5A55A] pl-3" data-id="element-702" data-mp="on6r86bq0yk7">
      <Icon className="w-5 h-5 text-[#C5A55A]" data-id="element-703" data-mp="ol3qoscxzn18" />
      <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-704" data-mp="jovgzuhikm1g">{title}</h4>
    </div>;
  const WorkplaceBlock = ({
    num,
    isRequired
  }: {
    num: number;
    isRequired: boolean;
  }) => {
    const p = `prev${num}`;
    const isExpanded = expandedWorkplaces[num] ?? false;
    const toggleExpand = () => {
      setExpandedWorkplaces(prev => ({
        ...prev,
        [num]: !prev[num]
      }));
    };
    const hasContent = formData[`${p}_name`];
    return <div className="rounded-xl border border-[#C5A55A]/10 overflow-hidden" data-id="element-705" data-mp="202bvheri355x">
        <button type="button" onClick={toggleExpand} className="w-full flex items-center justify-between p-4 bg-[#FFF8F0]/60 hover:bg-[#FFF8F0] transition-colors text-left" data-id="element-706" data-mp="20097t1dtlo4d">
          <div className="flex items-center gap-2" data-id="element-707" data-mp="3vfinv68s4qb1">
            <span className="text-xs font-semibold text-[#C5A55A] uppercase tracking-wide" data-id="element-708" data-mp="3qdjkhwd1hte3">
              Arbetsplats {num}
            </span>
            {isRequired && <span className="text-red-500 text-xs" data-id="element-709" data-mp="47efrtp8v3b6">*</span>}
            {!isRequired && <span className="text-xs text-[#1A1A1A]/40" data-id="element-710" data-mp="3cn7mxhxor2fk">(valfri)</span>}
            {hasContent && !isExpanded && <span className="text-xs text-[#1A1A1A]/50 ml-2" data-id="element-711" data-mp="37owf1cqyb1zz">
                – {formData[`${p}_name`]}
              </span>}
          </div>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-[#1A1A1A]/40" data-id="element-712" data-mp="3gp1ijbt0xg8v" /> : <ChevronDown className="w-4 h-4 text-[#1A1A1A]/40" data-id="element-713" data-mp="3gmyomgor32yv" />}
        </button>

        {isExpanded && <div className="space-y-4 p-5 bg-[#FFF8F0]/30 animate-in slide-in-from-top-1 duration-150" data-id="element-714" data-mp="2oxvjkyp1hol6">
            <div data-id="element-715" data-mp="2et5bsc6yfq91">
              <label className={labelClass} data-id="element-716" data-mp="2esnhhl3urm3r">
                Arbetsplatsens namn {isRequired && required}
              </label>
              <input type="text" name={`${p}_name`} required={isRequired} value={formData[`${p}_name`]} onChange={handleChange} className={inputClass} placeholder="T.ex. Café Konditori Solsidan" data-id="element-717" data-mp="2jlq0x0ex1f68" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-718" data-mp="32izkqio5kuo0">
              <div data-id="element-719" data-mp="32h6olxb61su9">
                <label className={labelClass} data-id="element-720" data-mp="2nm0i7985rs0e">
                  Referens – namn (chef/ansvarig) {isRequired && required}
                </label>
                <input type="text" name={`${p}_refName`} required={isRequired} value={formData[`${p}_refName`]} onChange={handleChange} className={inputClass} placeholder="Namn på chef eller ansvarig" data-id="element-721" data-mp="2sjs9c10zba3j" />
              </div>
              <div data-id="element-722" data-mp="3etquaxquypwz">
                <label className={labelClass} data-id="element-723" data-mp="3eujvlj71sn7q">
                  Referens – kontaktuppgifter {isRequired && required}
                </label>
                <input type="text" name={`${p}_refContact`} required={isRequired} value={formData[`${p}_refContact`]} onChange={handleChange} className={inputClass} placeholder="Telefon eller e-post" data-id="element-724" data-mp="3a0pfepqniqde" />
              </div>
            </div>

            <div data-id="element-725" data-mp="2r4g2w6vee7vi">
              <label className={labelClass} data-id="element-726" data-mp="2r60oh6zqigh8">
                Beskriv kort stället, dina arbetsuppgifter och din upplevelse{' '}
                {isRequired && required}
              </label>
              <textarea name={`${p}_summary`} required={isRequired} value={formData[`${p}_summary`]} onChange={handleChange} className={textareaClass} placeholder="T.ex. 'Litet familjeägt café i centrala Växjö. Jag jobbade som barista och bakade frallor på morgonen.'" data-id="element-727" data-mp="pbrw5njjp2az" />
            </div>
          </div>}
      </div>;
  };
  // === STEP 1: Job Description ===
  if (step === 'description') {
    const fd = job.fullDescription;
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-728" data-mp="8ywr7zbhi8ib">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-729" data-mp="iuq63pcoee39">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-730" data-mp="1uaericaa751y">
            <div className="flex items-start gap-4" data-id="element-731" data-mp="1z8q0kksr5bj4">
              <div className="text-4xl bg-[#FFF8F0] w-16 h-16 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-732" data-mp="1wvcrtx0bgx9u">
                {job.emoji}
              </div>
              <div data-id="element-733" data-mp="26tsftr41rqf4">
                <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-734" data-mp="26v3mbyovnc9x">
                  {job.title}
                </h3>
                <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide mb-1" data-id="element-735" data-mp="21te8yce6mmio">
                  {job.subtitle}
                </p>
                <p className="text-xs text-[#1A1A1A]/50" data-id="element-736" data-mp="2btew69f69ms5">
                  Top Notch Bakery Växjö
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-737" data-mp="2pk1xm9cbhqrx">
              <X className="w-6 h-6" data-id="element-738" data-mp="2zgn28tkfczv8" />
            </button>
          </div>

          {/* Description Body */}
          <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-739" data-mp="2uhtmsvlkuysf">
            {fd ? <div className="space-y-8" data-id="element-740" data-mp="3e3bxukbk2swc">
                {/* Intro */}
                <div className="space-y-4" data-id="element-741" data-mp="3i5qjtdaiu8ru">
                  {fd.intro.split('\n\n').map((paragraph, i) => <p key={i} className="text-[#1A1A1A]/80 leading-relaxed text-[15px]" data-id="element-742" data-mp="3470kdr08bx0b" data-mpi={i}>
                      {paragraph}
                    </p>)}
                </div>

                {/* Tasks */}
                <div className="space-y-4" data-id="element-743" data-mp="2z8z1b62szxou">
                  <div className="flex items-center gap-3 border-l-4 border-[#C5A55A] pl-3" data-id="element-744" data-mp="2lgr8tqijysvy">
                    <Briefcase className="w-5 h-5 text-[#C5A55A]" data-id="element-745" data-mp="2lexzu4kgvwks" />
                    <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-746" data-mp="2qgdixr333t0j">
                      Arbetsuppgifter
                    </h4>
                  </div>
                  <ul className="space-y-2.5 ml-1" data-id="element-747" data-mp="2gi7izf0u8wyb">
                    {fd.tasks.map((task, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#1A1A1A]/75 leading-relaxed" data-id="element-748" data-mp="1xdksr8myivzk" data-mpi={i}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5A55A] mt-2 shrink-0" data-id="element-749" data-mp="1nmzz7zm54hni" />
                        {task}
                      </li>)}
                  </ul>
                </div>

                {/* Requirements */}
                <div className="space-y-4" data-id="element-750" data-mp="2gb5mou4kwkr1">
                  <div className="flex items-center gap-3 border-l-4 border-[#C5A55A] pl-3" data-id="element-751" data-mp="2bd3thgdfv51u">
                    <Check className="w-5 h-5 text-[#C5A55A]" data-id="element-752" data-mp="26dhdiz687apc" />
                    <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-753" data-mp="26bwm3if7k7vp">
                      Vi söker dig som
                    </h4>
                  </div>
                  <ul className="space-y-2.5 ml-1" data-id="element-754" data-mp="uyucx8ffuklk">
                    {fd.requirements.map((req, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#1A1A1A]/75 leading-relaxed" data-id="element-755" data-mp="pyg3bly6v2lf" data-mpi={i}>
                        <Check className="w-4 h-4 text-[#C5A55A] mt-0.5 shrink-0" data-id="element-756" data-mp="36l61xexv5rpq" />
                        {req}
                      </li>)}
                  </ul>
                </div>

                {/* Call to action */}
                <div className="p-5 bg-[#FFF8F0] rounded-xl border border-[#C5A55A]/15" data-id="element-757" data-mp="3gd234yv8xxy6">
                  <p className="text-sm text-[#1A1A1A]/70 leading-relaxed italic" data-id="element-758" data-mp="3laann63vae2l">
                    {fd.callToAction}
                  </p>
                </div>
              </div> : <p className="text-[#1A1A1A]/70 leading-relaxed" data-id="element-759" data-mp="3ucbftok8u9xe">
                {job.description}
              </p>}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-760" data-mp="1zyiwz4g458cq">
            <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-761" data-mp="24wu5nbuo9ctd">
              Stäng
            </Button>
            <Button onClick={() => setStep('form')} className="min-w-[180px]" data-id="element-762" data-mp="kejkjdpm9cp1">
              <Send className="w-4 h-4 mr-2" data-id="element-763" data-mp="kgme27pz9nyi" />
              Gå vidare till ansökan
            </Button>
          </div>
        </div>
      </div>;
  }
  // === STEP 2: Application Form ===
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-764" data-mp="3nx36jyhpbmbe">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-765" data-mp="3sumj8tyr3r18">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-766" data-mp="1d5bgqb6om1cd">
          <div className="flex items-start gap-4" data-id="element-767" data-mp="1fnmzr4k0xzou">
            <div className="text-4xl bg-[#FFF8F0] w-16 h-16 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-768" data-mp="1fm22362ey0mr">
              {job.emoji}
            </div>
            <div data-id="element-769" data-mp="16o030w28pi24">
              <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-770" data-mp="16ojfvyd19g9a">
                {job.title}
              </h3>
              <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide mb-1" data-id="element-771" data-mp="1aqmqylvsiiu1">
                {job.subtitle}
              </p>
              <p className="text-xs text-[#1A1A1A]/50 hidden sm:block" data-id="element-772" data-mp="23dqz2zq6jda1">
                Ansökan till Top Notch Bakery Växjö
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-773" data-mp="1tnehngdnw084">
            <X className="w-6 h-6" data-id="element-774" data-mp="2m3gt3km4bv0w" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-775" data-mp="hjfajdw3jqzk">
          <form id="application-form" onSubmit={handleSubmit} className="space-y-10" data-id="element-776" data-mp="hjxeyxswxbik">
            {/* Section 1: Personal Info */}
            <div className="space-y-5" data-id="element-777" data-mp="7skmq537cjc0">
              <SectionHeading icon={User} title="Personuppgifter" data-id="element-778" data-mp="7rrro1dlvi9p" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-779" data-mp="cre26321uket">
                <div data-id="element-780" data-mp="3uy28fw8u5dm2">
                  <label htmlFor="name" className={labelClass} data-id="element-781" data-mp="3uxiqwgn3kr8k">
                    Namn {required}
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className={inputClass} placeholder="För- och efternamn" data-id="element-782" data-mp="3pxsjxj26mbn" />
                </div>
                <div data-id="element-783" data-mp="1o6kollllyj1w">
                  <label htmlFor="age" className={labelClass} data-id="element-784" data-mp="1o72p4ufazjfm">
                    Ålder {required}
                  </label>
                  <input type="number" id="age" name="age" required min="16" max="99" value={formData.age} onChange={handleChange} className={inputClass} placeholder="Din ålder" data-id="element-785" data-mp="33qtp2udlabsn" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-786" data-mp="3ndrcb19yb3zg">
                <div data-id="element-787" data-mp="58xsiqu34aye">
                  <label htmlFor="phone" className={labelClass} data-id="element-788" data-mp="584v4gh0mxsx">
                    Telefonnummer {required}
                  </label>
                  <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className={inputClass} placeholder="070-123 45 67" data-id="element-789" data-mp="aw8a2kxa5lc" />
                </div>
                <div data-id="element-790" data-mp="ayfv3zxti0ex">
                  <label htmlFor="email" className={labelClass} data-id="element-791" data-mp="axoblnhhvt50">
                    E-post {required}
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} placeholder="din@epost.se" data-id="element-792" data-mp="jy2yuse0rib5" />
                </div>
              </div>
              <div data-id="element-793" data-mp="yp3fhkox6iid">
                <label htmlFor="city" className={labelClass} data-id="element-794" data-mp="12ocbkw7fmsh3">
                  Bostadsort {required}
                </label>
                <input type="text" id="city" name="city" required value={formData.city} onChange={handleChange} className={inputClass} placeholder="T.ex. Växjö" data-id="element-795" data-mp="17ogka023miwr" />
              </div>
            </div>

            {/* Section 2: Work Preferences */}
            <div className="space-y-5" data-id="element-796" data-mp="1ctk8zwhoc6s6">
              <SectionHeading icon={Clock} title="Arbetstider & tillgänglighet" data-id="element-797" data-mp="1culoqe9mxhg8" />

              <div className="space-y-3" data-id="element-798" data-mp="25gwus4g0gakf">
                <label className={labelClass} data-id="element-799" data-mp="25gok8j7d1hce">
                  Hur mycket vill du jobba? {required}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-800" data-mp="20kfxokqu2lv7">
                  {['Heltid', 'Halvtid', 'Deltid', 'Extra / vid behov'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-801" data-mp="1rpji9agiscyw">
                        <input type="radio" name="workAmount" value={option} required checked={formData.workAmount === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-802" data-mp="24111t8d4eflp" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-803" data-mp="2ro94dij5zak1">{option}</span>
                      </label>)}
                </div>
              </div>

              {/* Conditional: Part-time / Extra details */}
              {showPartTimeFields && <div className="space-y-5 p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 animate-in slide-in-from-top-2 duration-200" data-id="element-804" data-mp="35ojwsq04gx7b">
                  <div data-id="element-805" data-mp="3afthdc6tdacc">
                    <label htmlFor="hoursPerWeek" className={labelClass} data-id="element-806" data-mp="3ah4pfo7al499">
                      Ungefär hur många timmar i veckan kan du jobba? {required}
                    </label>
                    <input type="text" id="hoursPerWeek" name="hoursPerWeek" required value={formData.hoursPerWeek} onChange={handleChange} className={inputClass} placeholder="T.ex. 10–15 timmar" data-id="element-807" data-mp="u0ez6yniwvsi" />
                  </div>
                  <div data-id="element-808" data-mp="b46vw0e2muzu">
                    <label htmlFor="availabilityTimes" className={labelClass} data-id="element-809" data-mp="pz39yu8kacku">
                      När är du tillgänglig? {required}
                    </label>
                    <textarea id="availabilityTimes" name="availabilityTimes" required value={formData.availabilityTimes} onChange={handleChange} className={textareaClass} placeholder="T.ex. helger och kvällar, förmiddagar mån–ons, eller skriv fritt vad som passar dig..." data-id="element-810" data-mp="kyyriyo3f7rz" />
                  </div>
                </div>}

              <div data-id="element-811" data-mp="2deq5slmbhb68">
                <label htmlFor="timePreferences" className={labelClass} data-id="element-812" data-mp="2ifw5j3ee9yp9">
                  Föredrar du vissa tider framför andra pga din livssituation?
                </label>
                <textarea id="timePreferences" name="timePreferences" value={formData.timePreferences} onChange={handleChange} className={textareaClass} placeholder="T.ex. morgnar passar bäst pga studier, eller inga kvällar pga familj..." data-id="element-813" data-mp="1kq15got359vj" />
              </div>

              <div data-id="element-814" data-mp="121dzm4334ggw">
                <label htmlFor="startDate" className={labelClass} data-id="element-815" data-mp="11ztgdam80jyc">
                  När kan du tänka dig att börja? {required}
                </label>
                <input type="date" id="startDate" name="startDate" required value={formData.startDate} onChange={handleChange} className={inputClass} data-id="element-816" data-mp="3k9z5mnpup4h3" />
              </div>
            </div>

            {/* Section 3: Role Specific Questions */}
            <div className="space-y-5" data-id="element-817" data-mp="31ceef9zm5ttb">
              <SectionHeading icon={Briefcase} title="Frågor om tjänsten" data-id="element-818" data-mp="31dfslnbxewf9" />
              <div className="space-y-5" data-id="element-819" data-mp="2wkmmow4q1i1l">
                {questions.map((q, idx) => <div key={idx} data-id="element-820" data-mp="2skw0l5q53q3e" data-mpi={idx}>
                    <label htmlFor={`q${idx + 1}`} className={labelClass} data-id="element-821" data-mp="2sk2t22jcwwk7">
                      {q} {required}
                    </label>
                    <textarea id={`q${idx + 1}`} name={`q${idx + 1}`} required value={formData[`q${idx + 1}`]} onChange={handleChange} className={textareaClass} data-id="element-822" data-mp="2im6gd6arttjz" />
                  </div>)}
              </div>
            </div>

            {/* Section 3b: Personality & Character */}
            <div className="space-y-5" data-id="element-823" data-mp="3sibngla739gb">
              <SectionHeading icon={User} title="Dig som person" data-id="element-824" data-mp="1c0fs6aojag0" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-825" data-mp="1ed7vca5j16s">
                <div data-id="element-826" data-mp="3irytk2ghq3xi">
                  <label htmlFor="strength" className={labelClass} data-id="element-827" data-mp="3irh0tft1e7uc">
                    Vad är din starkaste sida? {required}
                  </label>
                  <textarea id="strength" name="strength" required value={formData.strength} onChange={handleChange} className={textareaClass} style={{
                  minHeight: '70px'
                }} data-id="element-828" data-mp="39sv9av5l7ihf" />
                </div>
                <div data-id="element-829" data-mp="2l6nwuwtuejsh">
                  <label htmlFor="weakness" className={labelClass} data-id="element-830" data-mp="2l7ywckg995ds">
                    Vad är din svagaste sida? {required}
                  </label>
                  <textarea id="weakness" name="weakness" required value={formData.weakness} onChange={handleChange} className={textareaClass} style={{
                  minHeight: '70px'
                }} data-id="element-831" data-mp="2v5df4qpni699" />
                </div>
              </div>

              <div data-id="element-832" data-mp="3dweaz5vh5lol">
                <label htmlFor="definesYou" className={labelClass} data-id="element-833" data-mp="3dutgthd2o4r9">
                  Vad definierar dig som person? {required}
                </label>
                <textarea id="definesYou" name="definesYou" required value={formData.definesYou} onChange={handleChange} className={textareaClass} data-id="element-834" data-mp="38z4e555ra7qq" />
              </div>

              <div className="p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-5" data-id="element-835" data-mp="o1w679ln8zo7">
                <p className="text-sm text-[#1A1A1A]/60 italic" data-id="element-836" data-mp="j3cip4rejftq">
                  Beskriv i en mening hur följande personer skulle beskriva dig:
                </p>
                <div data-id="element-837" data-mp="a3z6w5ft2f5u">
                  <label htmlFor="friendDescribes" className={labelClass} data-id="element-838" data-mp="a6lc3yf84ikw">
                    Din bästa vän {required}
                  </label>
                  <input type="text" id="friendDescribes" name="friendDescribes" required value={formData.friendDescribes} onChange={handleChange} className={inputClass} placeholder="T.ex. 'Alltid den som får alla att skratta och känna sig välkomna'" data-id="element-839" data-mp="efmuwgcwf9e" />
                </div>
                <div data-id="element-840" data-mp="3dnakzr2a44dq">
                  <label htmlFor="employerDescribes" className={labelClass} data-id="element-841" data-mp="1t35ekdi1ia4s">
                    Din arbetsgivare {required}
                  </label>
                  <input type="text" id="employerDescribes" name="employerDescribes" required value={formData.employerDescribes} onChange={handleChange} className={inputClass} placeholder="T.ex. 'Pålitlig och tar alltid egna initiativ'" data-id="element-842" data-mp="1y0ouriyaswzd" />
                </div>
                <div data-id="element-843" data-mp="1f9ea3jj61gfx">
                  <label htmlFor="parentDescribes" className={labelClass} data-id="element-844" data-mp="1jcu2g3o7cumt">
                    Din förälder {required}
                  </label>
                  <input type="text" id="parentDescribes" name="parentDescribes" required value={formData.parentDescribes} onChange={handleChange} className={inputClass} placeholder="T.ex. 'Envis på bästa sätt och bryr sig alltid om andra'" data-id="element-845" data-mp="1o5n8qvzbkb10" />
                </div>
              </div>
            </div>

            {/* Section 4: Previous Workplaces */}
            <div className="space-y-5" data-id="element-846" data-mp="23pf3edrdbgle">
              <SectionHeading icon={Building2} title="Tidigare arbetsplatser" data-id="element-847" data-mp="23qgegi8dw334" />
              <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-848" data-mp="1fzaz046cvctc">
                Din senaste arbetsplats är obligatorisk. Lägg till fler om du
                vill.
              </p>
              <div className="space-y-3" data-id="element-849" data-mp="1p0izmswcgpql">
                <WorkplaceBlock num={1} isRequired={true} data-id="element-850" data-mp="1p1kd140u1obh" />

                {!showWorkplace2 ? <button type="button" onClick={() => {
                setShowWorkplace2(true);
                setExpandedWorkplaces(prev => ({
                  ...prev,
                  2: true
                }));
              }} className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-[#C5A55A]/25 rounded-xl text-xs font-medium text-[#C5A55A]/70 hover:text-[#C5A55A] hover:border-[#C5A55A]/40 hover:bg-[#FFF8F0]/50 transition-colors" data-id="element-851" data-mp="1kxur6mbjlku5">
                    <Plus className="w-3.5 h-3.5" data-id="element-852" data-mp="2yzjyqnhnfg9s" />
                    Lägg till arbetsplats 2
                  </button> : <WorkplaceBlock num={2} isRequired={false} data-id="element-853" data-mp="3mqfo7dymtzdv" />}

                {showWorkplace2 && !showWorkplace3 ? <button type="button" onClick={() => {
                setShowWorkplace3(true);
                setExpandedWorkplaces(prev => ({
                  ...prev,
                  3: true
                }));
              }} className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-[#C5A55A]/25 rounded-xl text-xs font-medium text-[#C5A55A]/70 hover:text-[#C5A55A] hover:border-[#C5A55A]/40 hover:bg-[#FFF8F0]/50 transition-colors" data-id="element-854" data-mp="3cyte5ky166cp">
                    <Plus className="w-3.5 h-3.5" data-id="element-855" data-mp="2cqmzny71fnj6" />
                    Lägg till arbetsplats 3
                  </button> : showWorkplace3 ? <WorkplaceBlock num={3} isRequired={false} data-id="element-856" data-mp="27u6dvanb9l99" /> : null}
              </div>
            </div>

            {/* Section 4b: Education */}
            <div className="space-y-5" data-id="element-857" data-mp="30i23jvegqcwj">
              <SectionHeading icon={GraduationCap} title="Utbildning & kompetens" data-id="element-858" data-mp="30j3jad6fbnkl" />
              <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-859" data-mp="2qrhasolh02lj">
                Berätta om utbildning, kurser eller annan kompetensstyrkande
                erfarenhet du vill lyfta fram.
              </p>
              <div className="space-y-4 p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10" data-id="element-860" data-mp="3jfd2tg03h6bz">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-861" data-mp="3rp10tkrsbrpj">
                  <div data-id="element-862" data-mp="3vpuk20ua8ic3">
                    <label htmlFor="edu_name" className={labelClass} data-id="element-863" data-mp="3vp2ryzzw92qk">
                      Utbildning / Kurs
                    </label>
                    <input type="text" id="edu_name" name="edu_name" value={formData.edu_name} onChange={handleChange} className={inputClass} placeholder="T.ex. Restaurang & Livsmedel, Barista-kurs" data-id="element-864" data-mp="4i8bpuwmbrow" />
                  </div>
                  <div data-id="element-865" data-mp="nat295ouhkpj">
                    <label htmlFor="edu_school" className={labelClass} data-id="element-866" data-mp="ncdom8109xcb">
                      Skola / Anordnare
                    </label>
                    <input type="text" id="edu_school" name="edu_school" value={formData.edu_school} onChange={handleChange} className={inputClass} placeholder="T.ex. Linnéuniversitetet, SFI Växjö" data-id="element-867" data-mp="x907wsdoogc8" />
                  </div>
                </div>
                <div data-id="element-868" data-mp="m9r4c30q2x4c">
                  <label htmlFor="edu_year" className={labelClass} data-id="element-869" data-mp="mb2a28dqaey3">
                    År / Period
                  </label>
                  <input type="text" id="edu_year" name="edu_year" value={formData.edu_year} onChange={handleChange} className={`${inputClass} max-w-[200px]`} placeholder="T.ex. 2022–2024" data-id="element-870" data-mp="v9ns82fafryc" />
                </div>
                <div data-id="element-871" data-mp="106nvy6oo0ima">
                  <label htmlFor="edu_description" className={labelClass} data-id="element-872" data-mp="36z6v4xzxyltz">
                    Beskriv kort vad du lärt dig eller varför det är relevant
                  </label>
                  <textarea id="edu_description" name="edu_description" value={formData.edu_description} onChange={handleChange} className={textareaClass} placeholder="T.ex. 'Lärde mig grunderna i livsmedelshygien och bakning. Fick även praktik på lokalt bageri.'" data-id="element-873" data-mp="3226oolz6jh2c" />
                </div>
              </div>
            </div>

            {/* Section 5: Personal Intro */}
            <div className="space-y-5" data-id="element-874" data-mp="ytxwa9y5hrvf">
              <SectionHeading icon={MessageSquare} title="Personligt intro" data-id="element-875" data-mp="12tf254hhor14" />

              <div className="p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10" data-id="element-876" data-mp="otnjurjxwohb">
                <p className="text-sm text-[#1A1A1A]/70 leading-relaxed mb-4" data-id="element-877" data-mp="tsgv0dbr83eb">
                  Denna ansökan fungerar som ditt CV och personliga brev i ett.
                  Nedan kan du lämna ett kort personligt intro som beskriver mer
                  om dig som person – sådant som inte redan nämnts ovan.
                </p>
                <label htmlFor="personalIntro" className={labelClass} data-id="element-878" data-mp="1aylmrrmc77n7">
                  Berätta lite mer om dig själv
                </label>
                <textarea id="personalIntro" name="personalIntro" value={formData.personalIntro} onChange={handleChange} className={`${textareaClass} min-h-[120px]`} placeholder="T.ex. intressen, egenskaper, vad som driver dig, eller något annat du vill att vi ska veta om dig..." data-id="element-879" data-mp="17062vwljyfe0" />
              </div>
            </div>

            {/* Error message */}
            {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-880" data-mp="jabrljgvsyvv">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-881" data-mp="2dn1oma2jqo7h" />
                <p data-id="element-882" data-mp="2dk5piieypthg">{submitError}</p>
              </div>}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-883" data-mp="2g77q8b3ou2kv">
          <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-884" data-mp="2l4zix7c5psq4">
            Avbryt
          </Button>
          <Button type="submit" form="application-form" isLoading={isSubmitting} className="min-w-[180px]" data-id="element-885" data-mp="2uvaxroxdpmbs">
            <Send className="w-4 h-4 mr-2" data-id="element-886" data-mp="226dfvmx2xrjk" />
            Skicka ansökan
          </Button>
        </div>
      </div>
    </div>;
}